/**
 * Servicio genérico de Firestore
 * 
 * Proporciona métodos CRUD abstractos para cualquier colección
 * Integra con StateManager y SyncManager
 * Fallback automático a localStorage si estamos offline
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { isOnline } from '../utils/network.js';
import { db, isFirebaseReady } from '../core/firebase.js';
import stateManager from '../core/stateManager.js';
import eventBus, { EVENTS } from '../core/eventBus.js';
import syncManager from '../core/syncManager.js';
import StorageService from './storage.service.js';

class FirestoreService {
  /**
   * Obtener todos los documentos de una colección
   * @param {string} collectionName - Nombre de la colección en Firestore
   * @param {Function} queryFn - Función para aplicar filters (opcional)
   * @param {Object} options - { entityName } para state/cache (si difiere de collectionName)
   * @returns {Promise<Object[]>} Array de documentos
   */
  static async findAll(collectionName, queryFn = null, options = {}) {
    const entityName = options.entityName || collectionName;
    if (!isFirebaseReady || !db) {
      console.warn('Firebase no disponible, usando cache local');
      return StorageService.get(`${entityName}_cache`, []);
    }
    try {
      const q = queryFn
        ? queryFn(query(collection(db, collectionName)))
        : collection(db, collectionName);

      const snapshot = await getDocs(q);
      const docs = [];

      snapshot.forEach(doc => {
        docs.push({
          id: doc.id,
          ...doc.data(),
          _firestoreRef: true
        });
      });

      return docs;
    } catch (error) {
      console.error(`Error obteniendo documentos de ${collectionName}:`, error);

      // Fallback a localStorage
      const cached = StorageService.get(`${entityName}_cache`, []);
      console.log(`⚠ Usando cache local de ${entityName}`);
      return cached;
    }
  }

  /**
   * Obtener un documento por ID
   * @param {string} collectionName - Nombre de la colección
   * @param {string} documentId - ID del documento
   * @param {Object} options - { entityName } para cache
   * @returns {Promise<Object|null>} El documento o null
   */
  static async findById(collectionName, documentId, options = {}) {
    const entityName = options.entityName || collectionName;
    if (!isFirebaseReady || !db) {
      console.warn('Firebase no disponible, usando cache local');
      return StorageService.get(`${entityName}_${documentId}`, null);
    }
    try {
      const docRef = doc(db, collectionName, documentId);
      const docSnapshot = await getDoc(docRef);

      if (!docSnapshot.exists()) {
        return null;
      }

      return {
        id: docSnapshot.id,
        ...docSnapshot.data(),
        _firestoreRef: true
      };
    } catch (error) {
      console.error(
        `Error obteniendo documento ${documentId} de ${collectionName}:`,
        error
      );

      // Fallback a cache local
      const cached = StorageService.get(`${entityName}_${documentId}`, null);
      return cached;
    }
  }

  /**
   * Crear un nuevo documento
   * @param {string} collectionName - Nombre de la colección
   * @param {Object} data - Datos del documento
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} El documento creado con su ID
   */
  static async create(collectionName, data, options = {}) {
    const entityName = options.entityName || collectionName;
    const offlineId = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    if (!isFirebaseReady || !db) {
      console.warn('Firebase no disponible, guardando offline');
      const offlineDoc = {
        id: offlineId,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _offline: true
      };
      stateManager.setEntity(entityName, offlineId, offlineDoc, 'create');
      syncManager.addOperation({
        type: 'create',
        collection: collectionName,
        data,
        offlineId,
        entityName
      });
      return offlineDoc;
    }
    try {
      // Preparar datos con timestamp del servidor
      const docData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, collectionName), docData);
      const createdDoc = {
        id: docRef.id,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _firestoreRef: true
      };

      // Sincronizar estado local
      stateManager.setEntity(entityName, docRef.id, createdDoc, 'create');

      // Actualizar cache
      this.#updateCollectionCache(entityName);

      return createdDoc;
    } catch (error) {
      console.error(`Error creando documento en ${collectionName}:`, error);

      // Si estamos offline, guardar con ID temporal y encolar para sincronización
      if (!isOnline()) {
        console.log(`⚠ Guardando offline con ID temporal: ${offlineId}`);

        const offlineDoc = {
          id: offlineId,
          ...data,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          _offline: true
        };

        stateManager.setEntity(entityName, offlineId, offlineDoc, 'create');

        // Encolar para sincronización después
        syncManager.addOperation({
          type: 'create',
          collection: collectionName,
          data,
          offlineId,
          entityName
        });

        return offlineDoc;
      }

      throw error;
    }
  }

  /**
   * Actualizar un documento existente
   * @param {string} collectionName - Nombre de la colección
   * @param {string} documentId - ID del documento
   * @param {Object} data - Datos a actualizar
   * @returns {Promise<Object>} El documento actualizado
   */
  static async update(collectionName, documentId, data, options = {}) {
    const entityName = options.entityName || collectionName;
    if (!isFirebaseReady || !db) {
      console.warn('Firebase no disponible, encolando actualización');
      const updated = { ...data, updatedAt: new Date().toISOString() };
      stateManager.setEntity(entityName, documentId, updated, 'update');
      syncManager.addOperation({
        type: 'update',
        collection: collectionName,
        documentId,
        data,
        entityName
      });
      return updated;
    }
    try {
      const docRef = doc(db, collectionName, documentId);
      const updateData = {
        ...data,
        updatedAt: serverTimestamp()
      };

      await updateDoc(docRef, updateData);

      const updated = {
        id: documentId,
        ...data,
        updatedAt: new Date().toISOString(),
        _firestoreRef: true
      };

      stateManager.setEntity(entityName, documentId, updated, 'update');
      this.#updateCollectionCache(entityName);

      return updated;
    } catch (error) {
      console.error(
        `Error actualizando documento ${documentId} en ${collectionName}:`,
        error
      );

      if (!isOnline()) {
        console.log(`⚠ Actualización encolada para sincronizar`);

        // Actualizar estado local
        const updated = {
          ...data,
          updatedAt: new Date().toISOString()
        };

        stateManager.setEntity(entityName, documentId, updated, 'update');

        // Encolar para sincronización
        syncManager.addOperation({
          type: 'update',
          collection: collectionName,
          documentId,
          data,
          entityName
        });

        return updated;
      }

      throw error;
    }
  }

  /**
   * Eliminar un documento
   * @param {string} collectionName - Nombre de la colección
   * @param {string} documentId - ID del documento a eliminar
   * @returns {Promise<boolean>} true si fue exitoso
   */
  static async delete(collectionName, documentId, options = {}) {
    const entityName = options.entityName || collectionName;
    if (!isFirebaseReady || !db) {
      console.warn('Firebase no disponible, encolando eliminación');
      stateManager.setEntity(entityName, documentId, null, 'delete');
      syncManager.addOperation({
        type: 'delete',
        collection: collectionName,
        documentId,
        entityName
      });
      return true;
    }
    try {
      const docRef = doc(db, collectionName, documentId);
      await deleteDoc(docRef);

      stateManager.setEntity(entityName, documentId, null, 'delete');
      this.#updateCollectionCache(entityName);

      return true;
    } catch (error) {
      console.error(
        `Error eliminando documento ${documentId} de ${collectionName}:`,
        error
      );

      if (!isOnline()) {
        console.log(`⚠ Eliminación encolada para sincronizar`);

        stateManager.setEntity(entityName, documentId, null, 'delete');

        syncManager.addOperation({
          type: 'delete',
          collection: collectionName,
          documentId,
          entityName
        });

        return true;
      }

      throw error;
    }
  }

  /**
   * Actualizar cache de colección en localStorage
   */
  static #updateCollectionCache(collectionName) {
    try {
      const docs = stateManager.getEntities(collectionName);
      StorageService.set(`${collectionName}_cache`, docs);
    } catch (error) {
      console.error('Error actualizando cache:', error);
    }
  }

  /**
   * Realizar query personalizado
   * @param {string} collectionName - Nombre de la colección
   * @param {Array} constraints - Array de constraints (where, orderBy, etc)
   * @returns {Promise<Object[]>} Documentos que coinciden
   */
  static async queryDocuments(collectionName, constraints = []) {
    if (!isFirebaseReady || !db) {
      console.warn('Firebase no disponible, retornando vacío');
      return [];
    }
    try {
      const q = query(collection(db, collectionName), ...constraints);
      const snapshot = await getDocs(q);
      const docs = [];

      snapshot.forEach(doc => {
        docs.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return docs;
    } catch (error) {
      console.error('Error en query:', error);
      return [];
    }
  }
}

export default FirestoreService;
